import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListCampaignRowsResult } from '@whinerp/client';
import { EmptyState, ErrorState, Num, Skeleton, type Column } from '@whinerp/ui';
import { formatRelativeDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { RecordList } from '../shared/record-list.tsx';
import { ViewBar } from '../shared/view-bar.js';
import { useSavedView, visibleColumns } from '../shared/use-saved-view.js';
import { nextSort } from '../shared/use-saved-view.js';

/**
 * SC-59 Campaigns, as a list.
 *
 * Beside the cards, not instead of them. A card carries what the campaign did
 * - members, sent, refused, opened - and each of those is a query, so the card
 * view is N+1 and unpaged. That is the right trade for a dozen campaigns and
 * the wrong one for the hundreds a workspace has after two years of sending.
 *
 * This view drops the per-campaign figures deliberately. "What did we send in
 * March", "which are still drafts", "everything on WhatsApp" are all questions
 * about the campaign rather than about its results, and none of them needs
 * four counts against every row.
 */

type Row = ListCampaignRowsResult['rows'][number];

function CampaignsTableScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({
    field: 'created',
    direction: 'desc',
  });
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1] ?? null;
  // A cursor is a position in one order, so changing the view or the grouping
  // makes the page it names a page of something else.
  const savedView = useSavedView(() => setCursorStack([]));

  const query = useQuery<ListCampaignRowsResult, ApiError>({
    queryKey: ['campaigns-list', { search, sort, cursor, view: savedView.params }],
    queryFn: () =>
      api.listCampaignRows({
        query: {
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(cursor ? { cursor } : {}),
          ...savedView.params,
          sort: sort.field,
          direction: sort.direction,
          limit: 50,
        },
      }),
  });

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'name',
        header: t('list.column.name'),
        sortField: 'name',
        essential: true,
        cell: (row) => row.name,
      },
      {
        key: 'channel',
        header: t('list.column.channel'),
        sortField: 'channel',
        cell: (row) => row.channel,
      },
      {
        key: 'status',
        header: t('list.column.status'),
        sortField: 'status',
        essential: true,
        cell: (row) => row.status,
      },
      {
        key: 'audience',
        header: t('list.column.audience'),
        sortField: 'audience',
        // The audience's NAME. A campaign row showing a uuid tells whoever
        // reads it nothing, and this is the column they scan for.
        cell: (row) => row.segmentName ?? '',
      },
      {
        key: 'sent',
        header: t('list.column.sent'),
        sortField: 'sentAt',
        cell: (row) => (
          <Num className="text-ink-meta">
            {row.sentAt ? formatRelativeDate(row.sentAt, format) : ''}
          </Num>
        ),
        hideOnCard: true,
      },
    ],
    [t, format],
  );

  const rows = query.data?.rows ?? [];
  const visible = useMemo(
    () => visibleColumns(columns, query.data?.columns ?? null),
    [columns, query.data?.columns],
  );

  /*
   * What a group heading says, per groupable field.
   *
   * Written out rather than indexed off the row: the API field name and the
   * row property are deliberately different - `audience` is `segmentName` -
   * and a lookup by string would be another place the two vocabularies have
   * to agree.
   */
  const groupLabel: Record<string, (row: Row) => string | null> = {
    // The same words the column shows. A heading reading "Sending" over rows
    // whose status column says "sending" is one vocabulary; translating here
    // and not there would be two, on the same screen, for the same value.
    status: (row) => row.status,
    channel: (row) => row.channel,
    owner: (row) => row.ownerName,
    audience: (row) => row.segmentName,
  };
  const groupBy = query.data?.groupBy ?? null;
  const grouping = groupBy ? groupLabel[groupBy] : undefined;

  // The groups the server says it will honour, labelled here: it decides what
  // works, the screen decides what it is called.
  const groupNames: Record<string, string> = {
    status: t('list.column.status'),
    channel: t('list.column.channel'),
    owner: t('campaign.owner'),
    audience: t('list.column.audience'),
  };
  const groupable = (query.data?.groupable ?? [])
    .filter((field) => Object.hasOwn(groupNames, field))
    .map((field) => ({ field, label: groupNames[field]! }));

  return (
    <RecordList<Row>
      title={t('list.campaigns.title')}
      icon="Megaphone"
      rowId={(row) => row.campaignId}
      recordHref={(row) => `/campaigns/${row.campaignId}`}
      columns={visible}
      {...(grouping ? { group: { label: grouping, emptyLabel: t('view.group.empty') } } : {})}
      viewBar={
        <ViewBar
          objectKey="campaign"
          active={savedView.active}
          draft={savedView.draft}
          columns={columns.map((column) => ({ key: column.key, label: column.header }))}
          groupable={groupable}
          onChange={savedView.onChange}
          onSelect={savedView.onSelect}
          onRemoveFilter={savedView.onRemoveFilter}
          onDiscard={savedView.onDiscard}
        />
      }
      page={
        query.data
          ? {
              rows,
              total: query.data.total,
              hasMore: query.data.hasMore,
              nextCursor: query.data.nextCursor,
            }
          : undefined
      }
      loading={query.isPending}
      error={query.isError ? { message: query.error.message, rule: query.error.rule } : null}
      onRetry={() => void query.refetch()}
      refreshing={query.isFetching}
      search={search}
      onSearchChange={(value) => {
        setSearch(value);
        setCursorStack([]);
      }}
      sort={sort}
      onSortChange={(field) => {
        setSort((current) => nextSort(current, field));
        setCursorStack([]);
      }}
      cursorStack={cursorStack}
      onCursorStackChange={setCursorStack}
      pageSize={query.data?.limit ?? 50}
      onClearFilters={() => {
        setSearch('');
        setCursorStack([]);
      }}
      filtered={search.trim().length > 0}
      aboveTable={
        <div className="flex justify-end">
          <Link data-campaigns-cards to="/campaigns" className="text-dense text-bronze-text hover:text-ink">
            {t('list.campaigns.cards')}
          </Link>
        </div>
      }
      emptyTitle={t('list.campaigns.empty.title')}
      emptyBody={t('list.campaigns.empty.body')}
      emptyIllustration="Megaphone"
    />
  );
}

function CampaignsTableEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Megaphone"
      title={t('list.campaigns.empty.title')}
      body={t('list.campaigns.empty.body')}
      secondary={{ label: t('list.campaigns.cards'), href: '/campaigns' }}
    />
  );
}

function CampaignsTableLoading(): JSX.Element {
  return (
    <div className="flex flex-col gap-px" role="status" aria-busy="true">
      <span className="sr-only">Loading campaigns</span>
      {Array.from({ length: 8 }, (_, index) => (
        <Skeleton key={index} className="h-row-comfortable" />
      ))}
    </div>
  );
}

function CampaignsTableError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Campaigns could not be loaded"
      body={error instanceof Error ? error.message : 'The list did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-59',
  route: '/campaigns/list',
  surface: 'page',
  title: 'Campaigns list',
  features: ['F-062'],
  entitlement: 'F-062',
  reachedBy: [{ kind: 'screen', from: 'SC-16', how: 'the "Show as a list" link above the cards' }],
  components: ['DataTable', 'FilterChips', 'ViewBar'],
  elements: ['RecordList'],
  states: {
    empty: CampaignsTableEmpty,
    loading: CampaignsTableLoading,
    error: CampaignsTableError,
  },
  component: CampaignsTableScreen,
});

export default screen;
